import { ResponsiveKpis } from '@/components/responsive-kpis';
import { TabStrip } from '@/components/tab-strip';
import { PageActions } from '@/components/page-actions';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { Fragment, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import {
  usePerformanceReportExport,
  useReportingQuery,
  useReportingQueryAll,
} from '@/api/hooks';
import type { ReportQueryRequest, ReportQueryResponse } from '@/api/types';
import { useCan } from '@/auth/capabilities';
import { DateFilter, Dropdown, SearchableDropdown } from '@/components/dropdowns';
import { QueryErrorNote } from '@/components/error-panel';
import {
  PerformanceTrendChart,
} from '@/components/performance-trend-chart';
import type { DirectPerformanceTrendPoint } from '@/components/performance-trend-chart';
import { ChannelPill, Icons } from '@/components/primitives';
import { ReportingDataControls } from '@/components/reporting-controls';
import { performanceMetricCell } from '@/components/performance-metric-cell';
import { csvFilename, triggerDownload } from '@/lib/csv';
import {
  calendarDayRange,
  fmtDateInput,
  nyDateInputToMs,
  todayNyInput,
} from '@/lib/datetime';
import {
  attributedConversions,
  attributedRevenueDollars,
  comparePerformanceMetrics,
  directPerformanceMetrics,
  emptyDirectPerformanceMetrics,
  grossSpendDollars,
  hasDirectAttributionEvidence,
  hasDirectDeliveryEvidence,
  performanceMetricValue,
} from '@/lib/performance-metrics';
import type {
  DirectPerformanceMetrics,
  PerformanceMetricId,
} from '@/lib/performance-metrics';
import { directPerformanceIntegrityError } from '@/lib/performance-integrity';
import {
  buildPerformanceReportFilters,
  buildPerformanceReportRequest,
  PERFORMANCE_TAB_GROUPS,
} from '@/lib/performance-report-request';
import type {
  PerformanceReportSearch,
  PerformanceReportSearchPatch,
  PerformanceReportTab,
  PerformanceSortDirection,
  PerformanceSortField,
} from '@/lib/performance-report-search';
import {
  serializePerformanceReportSearch,
  updatePerformanceReportSearch,
} from '@/lib/performance-report-search';
import { reportingPublishedDays, reportingZeroFillDays } from '@/lib/reporting-coverage';
import {
  reportMetricIsAvailable,
  reportMetricUnavailableReason,
  reportingMetricAvailability,
  currentDayTtdLaneNote,
} from '@/lib/reporting-availability';
import type {
  ReportMetricAvailability,
  ReportMetricFamily,
} from '@/lib/reporting-availability';

import './performance-report.css';

interface PerformanceReportProps {
  search: PerformanceReportSearch;
  onSearchChange: (
    patch: PerformanceReportSearchPatch,
    options?: { replace?: boolean },
  ) => void;
}

interface QueryState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

type ReportRow = ReportQueryResponse['rows'][number];
type ReportDimensions = ReportRow['dimensions'];

interface PerformanceRow {
  key: string;
  dimensions: ReportDimensions;
  metrics: DirectPerformanceMetrics;
}

interface DateState {
  preset: string;
  start: Date;
  end: Date;
}

interface DimensionOption {
  id: string;
  name: string;
  sublabel?: string;
}

export const REPORT_TAB_IDS = [
  'overview',
  'campaign',
  'production',
  'channel',
  'audience',
  'audienceType',
  'creative',
] as const satisfies readonly PerformanceReportTab[];

const REPORT_TABS: Array<{ id: PerformanceReportTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaign', label: 'By Campaign' },
  { id: 'production', label: 'By Assigned Production' },
  { id: 'channel', label: 'By Channel' },
  { id: 'audience', label: 'By Audience' },
  { id: 'audienceType', label: 'By Audience Type' },
  { id: 'creative', label: 'By Creative' },
];

const CHANNEL_OPTIONS = [
  { id: 'all', label: 'All channels' },
  { id: 'meta', label: 'Meta' },
  { id: 'ctv', label: 'CTV' },
  { id: 'dsp', label: 'Display' },
];

const AUDIENCE_TYPE_OPTIONS = [
  { id: 'all', label: 'All audience types' },
  { id: 'retargeting', label: 'Retargeting' },
  { id: 'direct', label: 'Direct' },
  { id: 'lookalike', label: 'Lookalike' },
];

const TARGET_OPTIONS = [
  { id: 'all', label: 'All destinations' },
  { id: 'meta', label: 'Meta' },
  { id: 'ttd', label: 'TTD' },
];

const TREND_GROUPS: Array<{
  label: string;
  metrics: Array<{ id: PerformanceMetricId; label: string }>;
}> = [
  {
    label: 'Volume',
    metrics: [
      { id: 'gross_spend', label: 'Gross spend' },
      { id: 'impressions', label: 'Impressions' },
      { id: 'clicks', label: 'Clicks' },
      { id: 'attributed_conversions', label: 'Tickets' },
      { id: 'attributed_revenue', label: 'Ticket Value' },
    ],
  },
  {
    label: 'Efficiency',
    metrics: [
      { id: 'ctr', label: 'CTR' },
      { id: 'cpm', label: 'CPM' },
      { id: 'cpc', label: 'CPC' },
      { id: 'cpa', label: 'CPA' },
      { id: 'roas', label: 'ROAS' },
    ],
  },
];

const TREND_OPTIONS = TREND_GROUPS.flatMap((group) => (
  group.metrics.map((metric) => ({ ...metric, section: group.label }))
));

const MIX_OPTIONS = [
  { id: 'attributed_revenue', label: 'Ticket Value' },
  { id: 'attributed_conversions', label: 'Tickets' },
  { id: 'gross_spend', label: 'Gross spend' },
];

const METRIC_COLUMNS: Array<{
  id: PerformanceMetricId;
  label: string;
  family: ReportMetricFamily;
}> = [
  { id: 'gross_spend', label: 'Gross spend', family: 'delivery' },
  { id: 'impressions', label: 'Impressions', family: 'delivery' },
  { id: 'clicks', label: 'Clicks', family: 'delivery' },
  { id: 'ctr', label: 'CTR', family: 'delivery' },
  { id: 'cpm', label: 'CPM', family: 'delivery' },
  { id: 'cpc', label: 'CPC', family: 'delivery' },
  { id: 'attributed_conversions', label: 'Tickets', family: 'attribution' },
  { id: 'attributed_revenue', label: 'Ticket Value', family: 'attribution' },
  { id: 'cpa', label: 'CPA', family: 'roas' },
  { id: 'roas', label: 'ROAS', family: 'roas' },
];

const PRIMARY_IDENTITY_COLUMN_WIDTH = 160;
const SECONDARY_IDENTITY_COLUMN_WIDTH = 100;
const PERFORMANCE_METRIC_COLUMN_WIDTH = 68;

function metricFamily(metric: PerformanceMetricId): ReportMetricFamily {
  if (['gross_spend', 'impressions', 'clicks', 'ctr', 'cpm', 'cpc'].includes(metric)) {
    return 'delivery';
  }
  if (metric === 'attributed_conversions' || metric === 'attributed_revenue') {
    return 'attribution';
  }
  return 'roas';
}

function reportRows(response: ReportQueryResponse | undefined): PerformanceRow[] {
  return (response?.rows ?? []).map((row) => ({
    key: JSON.stringify(row.dimensions),
    dimensions: row.dimensions,
    metrics: directPerformanceMetrics(row.metrics),
  }));
}

function trendPoints(
  response: ReportQueryResponse | undefined,
  from: string,
  to: string,
): DirectPerformanceTrendPoint[] {
  if (!response) return [];
  const byDay = new Map(
    response.rows.flatMap((row) => row.dimensions.day
      ? [[row.dimensions.day, directPerformanceMetrics(row.metrics)] as const]
      : []),
  );
  const deliveryPublished = reportingPublishedDays(response.freshness, 'delivery');
  const deliveryCovered = reportingZeroFillDays(response.freshness, 'delivery');
  const attributionPublished = reportingPublishedDays(response.freshness, 'attribution');
  return calendarDayRange(from, to).map((date) => {
    const metrics = byDay.get(date) ?? emptyDirectPerformanceMetrics();
    const deliveryAvailable = deliveryPublished.has(date)
      || deliveryCovered.has(date)
      || hasDirectDeliveryEvidence(metrics);
    const directAttributionAvailable = attributionPublished.has(date)
      || hasDirectAttributionEvidence(metrics);
    return {
      date,
      deliveryAvailable,
      directAttributionAvailable,
      grossSpendCents: metrics.gross_spend_cents,
      attributionGrossSpendCents: directAttributionAvailable
        ? metrics.attribution_gross_spend_cents
        : null,
      impressions: metrics.impressions,
      clicks: metrics.clicks,
      directConversions: attributedConversions(metrics),
      directRevenueCents: attributedRevenueDollars(metrics) * 100,
    };
  });
}

function dimensionColumns(tab: PerformanceReportTab): Array<{
  id: string;
  label: string;
  render: (row: PerformanceRow) => ReactNode;
}> {
  const campaign = {
    id: 'campaign',
    label: 'Campaign',
    render: (row: PerformanceRow) => row.dimensions.campaign_name
      ?? row.dimensions.campaign_id
      ?? 'Unknown campaign',
  };
  const production = {
    id: 'production',
    label: 'Assigned production',
    render: (row: PerformanceRow) => row.dimensions.assigned_production_name
      ?? row.dimensions.assigned_production_id
      ?? 'Unknown production',
  };
  const channel = {
    id: 'channel',
    label: 'Channel',
    render: (row: PerformanceRow) => row.dimensions.channel
      ? <ChannelPill channel={row.dimensions.channel}/>
      : <span className="dim">—</span>,
  };
  switch (tab) {
    case 'overview':
    case 'production':
      return [production];
    case 'campaign':
      return [campaign, production, channel];
    case 'channel':
      return [channel];
    case 'audience':
      return [
        {
          id: 'audience',
          label: 'Audience',
          render: (row) => {
            const name = row.dimensions.audience_name
              ?? row.dimensions.audience_id
              ?? 'Unknown audience';
            const revision = row.dimensions.audience_revision;
            return (
              <span
                title={revision == null ? name : `${name} · Revision ${revision}`}
                aria-label={revision == null ? name : `${name}, revision ${revision}`}
              >
                {name}
              </span>
            );
          },
        },
        {
          id: 'audience-type',
          label: 'Audience type',
          render: (row) => row.dimensions.audience_type ?? '—',
        },
      ];
    case 'audienceType':
      return [{
        id: 'audience-type',
        label: 'Audience type',
        render: (row) => row.dimensions.audience_type ?? '—',
      }];
    case 'creative':
      return [
        {
          id: 'creative',
          label: 'Creative',
          // Name plus the revision that actually delivered these numbers.
          // A roll-out does not bump the campaign's plan revision, so the
          // revision is the only thing separating the version a campaign
          // rolled away from and the one it rolled onto. Rows written before
          // the reporting key carried one show the name alone, which is
          // honest: the revision is unknown, not 1.
          render: (row) => {
            const name = row.dimensions.creative_name
              ?? row.dimensions.creative_id
              ?? 'Unknown / unmapped creative';
            const revision = row.dimensions.creative_revision;
            if (revision == null) return <span title={name}>{name}</span>;
            return (
              <span
                title={`${name} · Revision ${revision}`}
                aria-label={`${name}, revision ${revision}`}
              >
                {name} <span className="muted">v{revision}</span>
              </span>
            );
          },
        },
        {
          id: 'format',
          label: 'Format',
          render: (row) => [
            row.dimensions.creative_media_type,
            row.dimensions.creative_format,
          ].filter(Boolean).join(' · ') || '—',
        },
        production,
      ];
  }
}

function FilterField({
  label,
  children,
  isDefaultSelection = false,
}: {
  label: string;
  children: ReactNode;
  isDefaultSelection?: boolean;
}) {
  return (
    <label className={`performance-filter-field${isDefaultSelection ? ' is-default-selection' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function optionRows(
  response: ReportQueryResponse | undefined,
  id: keyof ReportDimensions,
  name: keyof ReportDimensions,
  sublabel?: (dimensions: ReportDimensions) => string | undefined,
): DimensionOption[] {
  const options = new Map<string, DimensionOption>();
  for (const row of response?.rows ?? []) {
    const optionId = row.dimensions[id];
    if (typeof optionId !== 'string' || !optionId) continue;
    const optionName = row.dimensions[name];
    options.set(optionId, {
      id: optionId,
      name: typeof optionName === 'string' && optionName ? optionName : optionId,
      sublabel: sublabel?.(row.dimensions),
    });
  }
  return [...options.values()].sort((left, right) => (
    left.name.localeCompare(right.name) || left.id.localeCompare(right.id)
  ));
}

function searchableOptions(
  options: readonly DimensionOption[],
): Array<{ id: string; label: string; sublabel?: string }> {
  return options.map((option) => ({
    id: option.id,
    label: option.name,
    sublabel: option.sublabel,
  }));
}

function MetricUnavailable({ reason }: { reason: string }) {
  return <span className="performance-unavailable" title={reason}>—</span>;
}

function metricIsAvailable(
  availability: ReportMetricAvailability,
  metric: PerformanceMetricId,
): boolean {
  return reportMetricIsAvailable(availability, metricFamily(metric));
}

function kpiValue(
  metrics: DirectPerformanceMetrics,
  metric: PerformanceMetricId,
  availability: ReportMetricAvailability,
): ReactNode {
  if (!metricIsAvailable(availability, metric)) {
    return <MetricUnavailable reason={reportMetricUnavailableReason(availability, metricFamily(metric))}/>;
  }
  return performanceMetricCell(metrics, metric);
}

function PerformanceKpis({
  response,
  availability,
}: {
  response: ReportQueryResponse;
  availability: ReportMetricAvailability;
}) {
  const metrics = directPerformanceMetrics(response.totals);
  const kpis: Array<{ metric: PerformanceMetricId; label: string }> = [
    { metric: 'gross_spend', label: 'Gross spend' },
    { metric: 'impressions', label: 'Impressions' },
    { metric: 'clicks', label: 'Clicks' },
    { metric: 'attributed_conversions', label: 'Tickets' },
    { metric: 'attributed_revenue', label: 'Ticket Value' },
    { metric: 'roas', label: 'ROAS' },
  ];
  return (
    <ResponsiveKpis><div className="kpi-strip performance-kpis" aria-label="Performance summary">
      {kpis.map(({ metric, label }) => (
        <div className="kpi" key={metric}>
          <div className="kpi-label">{label}</div>
          <div className="kpi-val">{kpiValue(metrics, metric, availability)}</div>
        </div>
      ))}
    </div></ResponsiveKpis>
  );
}

function PerformanceTrendPanel({
  points,
  metric,
  onMetricChange,
}: {
  points: DirectPerformanceTrendPoint[];
  metric: PerformanceMetricId;
  onMetricChange: (metric: PerformanceMetricId) => void;
}) {
  return (
    <section className="performance-panel performance-trend-panel" aria-labelledby="performance-trend-title">
      <div className="performance-panel-heading">
        <div>
          <h2 id="performance-trend-title">Performance over time</h2>
          <p>Daily aggregate for the current report filters.</p>
        </div>
        <div className="performance-metric-select">
          <span>Metric</span>
          <Dropdown
            label=""
            value={metric}
            options={TREND_OPTIONS}
            onChange={(value: string) => onMetricChange(value as PerformanceMetricId)}
            ariaLabel="Performance trend metric"
            width={190}
            rightAlign
          />
        </div>
      </div>
      <PerformanceTrendChart
        points={points}
        metric={metric}
        ariaLabel="Daily ticket performance"
      />
    </section>
  );
}

function sortableHeader(
  column: { id: PerformanceMetricId; label: string },
  search: PerformanceReportSearch,
  onSearchChange: PerformanceReportProps['onSearchChange'],
) {
  const active = search.sortField === column.id;
  const ariaSort = active
    ? search.sortDirection === 'asc' ? 'ascending' : 'descending'
    : 'none';
  return (
    <th
      key={column.id}
      className="performance-number performance-metric-column"
      aria-sort={ariaSort}
    >
      <button
        type="button"
        className="performance-sort"
        onClick={() => onSearchChange({
          sortField: column.id,
          sortDirection: active && search.sortDirection === 'desc' ? 'asc' : 'desc',
        })}
      >
        {column.label}
        <span aria-hidden="true">{active ? (search.sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}</span>
      </button>
    </th>
  );
}

function PerformanceTable({
  title,
  tab,
  response,
  query,
  search,
  onSearchChange,
  empty = 'No rows match the current report filters.',
}: {
  title: string;
  tab: PerformanceReportTab;
  response: ReportQueryResponse | undefined;
  query: QueryState;
  search: PerformanceReportSearch;
  onSearchChange: PerformanceReportProps['onSearchChange'];
  empty?: string;
}) {
  const availability = reportingMetricAvailability('performance', response?.freshness);
  const dimensions = dimensionColumns(tab);
  const rows = useMemo(() => reportRows(response).sort((left, right) => (
    comparePerformanceMetrics(
      left.metrics,
      right.metrics,
      search.sortField,
      search.sortDirection,
    ) || left.key.localeCompare(right.key)
  )), [response, search.sortDirection, search.sortField]);
  const dimensionWidths = dimensions.map((_, index) => (
    index === 0 ? PRIMARY_IDENTITY_COLUMN_WIDTH : SECONDARY_IDENTITY_COLUMN_WIDTH
  ));
  const dimensionOffsets = dimensionWidths.map((_, index) => (
    dimensionWidths.slice(0, index).reduce((sum, width) => sum + width, 0)
  ));
  const tableWidth = dimensionWidths.reduce((sum, width) => sum + width, 0)
    + METRIC_COLUMNS.length * PERFORMANCE_METRIC_COLUMN_WIDTH;
  const columnCount = dimensions.length + METRIC_COLUMNS.length;
  return (
    <section className="performance-panel performance-table-panel" aria-labelledby={`table-${tab}`}>
      <div className="performance-panel-heading">
        <div>
          <h2 id={`table-${tab}`}>{title}</h2>
          <p>{rows.length.toLocaleString()} {rows.length === 1 ? 'row' : 'rows'} · sorted by {METRIC_COLUMNS.find((item) => item.id === search.sortField)?.label}</p>
        </div>
      </div>
      <div className="performance-table-scroll" tabIndex={0} aria-label={`${title} performance metrics`}>
        <table
          className="tbl performance-table"
          style={{ width: '100%', minWidth: tableWidth }}
        >
          <colgroup>
            {dimensions.map((column, index) => (
              <col key={column.id} style={{ width: dimensionWidths[index] }}/>
            ))}
            {METRIC_COLUMNS.map((column) => (
              <col key={column.id} style={{ width: PERFORMANCE_METRIC_COLUMN_WIDTH }}/>
            ))}
          </colgroup>
          <thead>
            <tr>
              {dimensions.map((column, index) => (
                <th
                  key={column.id}
                  className="performance-sticky-identity performance-identity"
                  style={{
                    left: dimensionOffsets[index],
                    width: dimensionWidths[index],
                    minWidth: dimensionWidths[index],
                    maxWidth: dimensionWidths[index],
                  }}
                >
                  {column.label}
                </th>
              ))}
              {METRIC_COLUMNS.map((column) => sortableHeader(column, search, onSearchChange))}
            </tr>
          </thead>
          <tbody>
            {query.isError ? (
              <tr><td colSpan={columnCount}><QueryErrorNote error={query.error} what={title} onRetry={() => void query.refetch()}/></td></tr>
            ) : query.isLoading ? (
              <tr><td colSpan={columnCount} className="performance-empty">Loading report…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columnCount} className="performance-empty">{empty}</td></tr>
            ) : rows.map((row) => (
              <tr key={row.key}>
                {dimensions.map((column, index) => (
                  <td
                    key={column.id}
                    className="performance-sticky-identity performance-identity"
                    style={{
                      left: dimensionOffsets[index],
                      width: dimensionWidths[index],
                      minWidth: dimensionWidths[index],
                      maxWidth: dimensionWidths[index],
                    }}
                  >
                    {column.render(row)}
                  </td>
                ))}
                {METRIC_COLUMNS.map((column) => (
                  <td key={column.id} className="performance-number performance-metric-column">
                    {metricIsAvailable(availability, column.id)
                      ? performanceMetricCell(row.metrics, column.id)
                      : <MetricUnavailable reason={reportMetricUnavailableReason(availability, column.family)}/>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ChannelMix({
  response,
  metric,
  onMetricChange,
}: {
  response: ReportQueryResponse | undefined;
  metric: 'gross_spend' | 'attributed_conversions' | 'attributed_revenue';
  onMetricChange: (metric: 'gross_spend' | 'attributed_conversions' | 'attributed_revenue') => void;
}) {
  const rows = reportRows(response).filter((row) => row.dimensions.channel);
  const availability = reportingMetricAvailability('performance', response?.freshness);
  const available = metricIsAvailable(availability, metric);
  const magnitude = available
    ? rows.reduce((sum, row) => sum + Math.abs(performanceMetricValue(row.metrics, metric) ?? 0), 0)
    : 0;
  const sorted = [...rows].sort((left, right) => (
    Math.abs(performanceMetricValue(right.metrics, metric) ?? 0)
      - Math.abs(performanceMetricValue(left.metrics, metric) ?? 0)
  ));
  return (
    <section className="performance-panel" aria-labelledby="channel-mix-title">
      <div className="performance-panel-heading">
        <div>
          <h2 id="channel-mix-title">Channel mix</h2>
          <p>Bar share uses absolute activity; signed corrections retain their direction.</p>
        </div>
        <div className="seg" aria-label="Channel mix metric">
          {MIX_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={metric === option.id ? 'on' : ''}
              aria-pressed={metric === option.id}
              onClick={() => onMetricChange(option.id as typeof metric)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="performance-channel-list">
        {!available ? (
          <div className="performance-empty">{reportMetricUnavailableReason(availability, metricFamily(metric))}</div>
        ) : sorted.length === 0 ? (
          <div className="performance-empty">No channel activity in this scope.</div>
        ) : sorted.map((row) => {
          const value = performanceMetricValue(row.metrics, metric) ?? 0;
          const percent = magnitude > 0 ? Math.abs(value) / magnitude * 100 : 0;
          return (
            <div className="performance-channel-row" key={row.key}>
              <div className="performance-channel-name"><ChannelPill channel={row.dimensions.channel!}/></div>
              <div className="performance-channel-bar"><span style={{ width: `${percent}%` }}/></div>
              <div className="performance-channel-primary">{performanceMetricCell(row.metrics, metric)}</div>
              <div className="performance-channel-support">
                {performanceMetricCell(row.metrics, 'attributed_conversions')} Tickets · {performanceMetricCell(row.metrics, 'attributed_revenue')} Ticket Value · {performanceMetricCell(row.metrics, 'roas')} ROAS
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ActiveFilterChips({
  search,
  productionOptions,
  campaignOptions,
  audienceOptions,
  creativeOptions,
  onSearchChange,
}: {
  search: PerformanceReportSearch;
  productionOptions: DimensionOption[];
  campaignOptions: DimensionOption[];
  audienceOptions: DimensionOption[];
  creativeOptions: DimensionOption[];
  onSearchChange: PerformanceReportProps['onSearchChange'];
}) {
  const name = (options: DimensionOption[], id: string | undefined) => (
    options.find((option) => option.id === id)?.name ?? id
  );
  const chips: Array<{ label: string; clear: PerformanceReportSearchPatch }> = [];
  if (search.productionId) chips.push({ label: `Show: ${name(productionOptions, search.productionId)}`, clear: { productionId: undefined } });
  if (search.campaignId) chips.push({ label: `Campaign: ${name(campaignOptions, search.campaignId)}`, clear: { campaignId: undefined } });
  if (search.target) chips.push({ label: `Destination: ${search.target.toUpperCase()}`, clear: { target: undefined } });
  if (search.channel) chips.push({ label: `Channel: ${search.channel.toUpperCase()}`, clear: { channel: undefined } });
  if (search.audienceId) chips.push({ label: `Audience: ${name(audienceOptions, `${search.audienceId}:${search.audienceRevision}`)}`, clear: { audienceId: undefined, audienceRevision: undefined } });
  if (search.audienceType) chips.push({ label: `Audience type: ${search.audienceType}`, clear: { audienceType: undefined } });
  if (search.creativeId) chips.push({ label: `Creative: ${name(creativeOptions, search.creativeId)}`, clear: { creativeId: undefined } });
  if (!chips.length) return null;
  const clearAll: PerformanceReportSearchPatch = {
    productionId: undefined,
    campaignId: undefined,
    target: undefined,
    channel: undefined,
    audienceId: undefined,
    audienceRevision: undefined,
    audienceType: undefined,
    creativeId: undefined,
  };
  return (
    <div className="performance-filter-chips" aria-label="Active report filters">
      {chips.map((chip) => (
        <button type="button" key={chip.label} onClick={() => onSearchChange(chip.clear)}>
          {chip.label}<span aria-hidden="true"> ×</span>
        </button>
      ))}
      <button type="button" className="performance-clear-all" onClick={() => onSearchChange(clearAll)}>Clear all</button>
    </div>
  );
}

export function PerformanceReport({ search, onSearchChange }: PerformanceReportProps) {
  const canExport = useCan('reports_export');
  const filters = useMemo(() => buildPerformanceReportFilters(search), [search]);
  const emptyFilters = useMemo(() => buildPerformanceReportFilters({
    ...search,
    productionId: undefined,
    campaignId: undefined,
    target: undefined,
    channel: undefined,
    audienceId: undefined,
    audienceRevision: undefined,
    audienceType: undefined,
    creativeId: undefined,
  }), [search]);
  const date = useMemo<DateState>(() => ({
    preset: 'custom',
    start: new Date(nyDateInputToMs(search.from)),
    end: new Date(nyDateInputToMs(search.to)),
  }), [search.from, search.to]);

  const optionRequest = (groupBy: ReportQueryRequest['group_by']) => (
    buildPerformanceReportRequest(search, groupBy, emptyFilters, 500, false)
  );
  const campaignOptionsQuery = useReportingQueryAll(optionRequest(['campaign']));
  const productionOptionsQuery = useReportingQueryAll(optionRequest(['assigned_production']));
  const audienceOptionsQuery = useReportingQueryAll(optionRequest(['audience']));
  const creativeOptionsQuery = useReportingQueryAll(optionRequest(['creative']));

  const campaignOptions = useMemo(() => optionRows(campaignOptionsQuery.data, 'campaign_id', 'campaign_name'), [campaignOptionsQuery.data]);
  const productionOptions = useMemo(() => optionRows(productionOptionsQuery.data, 'assigned_production_id', 'assigned_production_name'), [productionOptionsQuery.data]);
  const creativeOptions = useMemo(() => optionRows(creativeOptionsQuery.data, 'creative_id', 'creative_name', (dimensions) => [dimensions.creative_media_type, dimensions.creative_format].filter(Boolean).join(' · ') || undefined), [creativeOptionsQuery.data]);
  const audienceOptions = useMemo<DimensionOption[]>(() => {
    const options = new Map<string, DimensionOption>();
    for (const row of audienceOptionsQuery.data?.rows ?? []) {
      const id = row.dimensions.audience_id;
      const revision = row.dimensions.audience_revision;
      if (!id || revision == null) continue;
      const key = `${id}:${revision}`;
      options.set(key, {
        id: key,
        name: row.dimensions.audience_name ?? id,
        sublabel: `Revision ${revision}${row.dimensions.audience_type ? ` · ${row.dimensions.audience_type}` : ''}`,
      });
    }
    return [...options.values()].sort((left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id));
  }, [audienceOptionsQuery.data]);

  const trendRequest = useMemo(() => buildPerformanceReportRequest(search, ['day'], filters, 500, false), [filters, search]);
  const tableRequest = useMemo(() => buildPerformanceReportRequest(search, PERFORMANCE_TAB_GROUPS[search.tab], filters), [filters, search]);
  const channelRequest = useMemo(() => buildPerformanceReportRequest(search, ['channel'], filters, 500, false), [filters, search]);
  const productionRequest = useMemo(() => buildPerformanceReportRequest(search, ['assigned_production'], filters), [filters, search]);
  const trendQuery = useReportingQuery(trendRequest);
  const tableQuery = useReportingQueryAll(tableRequest);
  const channelQuery = useReportingQuery(channelRequest, search.tab === 'overview');
  const productionQuery = useReportingQueryAll(productionRequest, search.tab === 'overview');
  const exportMutation = usePerformanceReportExport();

  const visibleResponses = [
    trendQuery.data,
    tableQuery.data,
    ...(search.tab === 'overview' ? [channelQuery.data, productionQuery.data] : []),
  ];
  const integrityError = visibleResponses
    .map((response) => directPerformanceIntegrityError(response))
    .find((error): error is string => Boolean(error)) ?? null;
  const availability = reportingMetricAvailability('performance', trendQuery.data?.freshness);
  const points = useMemo(() => trendPoints(trendQuery.data, search.from, search.to), [search.from, search.to, trendQuery.data]);
  const queryError = [trendQuery, tableQuery, channelQuery, productionQuery]
    .find((query) => query.isError && (query === trendQuery || query === tableQuery || search.tab === 'overview'));

  const exportActive = () => {
    if (integrityError || !trendQuery.data || !tableQuery.data) return;
    exportMutation.mutate({ ...tableRequest, cursor: null, limit: null }, {
      onSuccess: (csv) => triggerDownload(
        csvFilename(`performance-${search.tab}`),
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
      ),
    });
  };
  const tabTitle = REPORT_TABS.find((tab) => tab.id === search.tab)?.label ?? 'Performance';
  const baseIntegrityReady = Boolean(trendQuery.data && tableQuery.data && !integrityError);
  const todayNy = todayNyInput();
  const includesToday = search.from <= todayNy && todayNy <= search.to;
  const ttdLaneNote = currentDayTtdLaneNote(trendQuery.data?.freshness);

  return (
    <div className="performance-report">
      <div className="page-hd performance-page-heading">
        <h1 className="page-title">Performance</h1>
        <PageActions>
          <ReportingDataControls
            data-phone="meta"
            freshness={trendQuery.data?.freshness}
            dataset="performance"
            highlightAge
          />
          <DateFilter
            completedDaysOnly
            value={date}
            onChange={(range: DateState) => onSearchChange({
              from: fmtDateInput(range.start),
              to: fmtDateInput(range.end),
            })}
          />
          <button
            type="button"
            className="btn btn-icon-phone"
            data-phone="inline"
            aria-label="Export CSV"
            onClick={exportActive}
            disabled={!canExport || exportMutation.isPending || !baseIntegrityReady}
            title={!canExport
              ? 'You do not have permission to export reports'
              : integrityError
                ?? 'Export this filtered view'}
          >
            <span>{Icons.dl}</span><span className="phone-long">{exportMutation.isPending ? 'Exporting…' : 'Export CSV'}</span>
          </button>
        </PageActions>
      </div>

      {integrityError && <div className="performance-integrity-error" role="alert">{integrityError}</div>}
      {queryError && !integrityError && <QueryErrorNote error={queryError.error} what="Performance report" onRetry={() => void queryError.refetch()}/>}
      {exportMutation.isError && <QueryErrorNote error={exportMutation.error} what="Performance CSV export" onRetry={exportActive}/>}
      {includesToday && (
        <div className="performance-provisional" role="status">
          Today is included. Current-day delivery is provisional; Tickets, Ticket Value, CPA, and ROAS use completed ET days only.
          {ttdLaneNote && <> {ttdLaneNote}</>}
        </div>
      )}

      <ResponsiveFilters className="performance-filter-bar">
        <FilterField label="Production">
          <SearchableDropdown
            value={search.productionId ?? ''}
            options={searchableOptions(productionOptions)}
            emptyOptionLabel="All productions"
            onChange={(value) => onSearchChange({ productionId: value || undefined })}
            placeholder="All productions"
            searchPlaceholder="Search productions…"
            width="100%"
            size="sm"
          />
        </FilterField>
        <FilterField label="Campaign">
          <SearchableDropdown
            value={search.campaignId ?? ''}
            options={searchableOptions(campaignOptions)}
            emptyOptionLabel="All campaigns"
            onChange={(value) => onSearchChange({ campaignId: value || undefined })}
            placeholder="All campaigns"
            searchPlaceholder="Search campaigns…"
            width="100%"
            size="sm"
          />
        </FilterField>
        <FilterField label="Destination" isDefaultSelection={!search.target}>
          <Dropdown
            label=""
            value={search.target ?? 'all'}
            options={TARGET_OPTIONS}
            onChange={(value: string) => onSearchChange({ target: value === 'all' ? undefined : value as PerformanceReportSearch['target'] })}
          />
        </FilterField>
        <FilterField label="Channel" isDefaultSelection={!search.channel}>
          <Dropdown
            label=""
            value={search.channel ?? 'all'}
            options={CHANNEL_OPTIONS}
            onChange={(value: string) => onSearchChange({ channel: value === 'all' ? undefined : value as PerformanceReportSearch['channel'] })}
          />
        </FilterField>
        <FilterField label="Audience / revision">
          <SearchableDropdown
            value={search.audienceId ? `${search.audienceId}:${search.audienceRevision}` : ''}
            options={searchableOptions(audienceOptions)}
            emptyOptionLabel="All audiences"
            onChange={(value) => {
              const match = /^(.*):(\d+)$/.exec(value);
              onSearchChange(match
                ? { audienceId: match[1], audienceRevision: Number(match[2]) }
                : { audienceId: undefined, audienceRevision: undefined });
            }}
            placeholder="All audiences"
            searchPlaceholder="Search audiences…"
            width="100%"
            size="sm"
          />
        </FilterField>
        <FilterField label="Audience type" isDefaultSelection={!search.audienceType}>
          <Dropdown
            label=""
            value={search.audienceType ?? 'all'}
            options={AUDIENCE_TYPE_OPTIONS}
            onChange={(value: string) => onSearchChange({ audienceType: value === 'all' ? undefined : value as PerformanceReportSearch['audienceType'] })}
          />
        </FilterField>
        <FilterField label="Creative">
          <SearchableDropdown
            value={search.creativeId ?? ''}
            options={searchableOptions(creativeOptions)}
            emptyOptionLabel="All creatives"
            onChange={(value) => onSearchChange({ creativeId: value || undefined })}
            placeholder="All creatives"
            searchPlaceholder="Search creatives…"
            width="100%"
            size="sm"
          />
        </FilterField>
      </ResponsiveFilters>
      <ActiveFilterChips
        search={search}
        productionOptions={productionOptions}
        campaignOptions={campaignOptions}
        audienceOptions={audienceOptions}
        creativeOptions={creativeOptions}
        onSearchChange={onSearchChange}
      />

      <div className="performance-tabs-wrap">
        <TabStrip className="report-tabs" role="tablist" aria-label="Performance report views">
          {REPORT_TABS.map((tab) => (
            <Link
              to="/reports"
              search={serializePerformanceReportSearch(
                updatePerformanceReportSearch(search, { tab: tab.id }),
              )}
              preload="intent"
              role="tab"
              aria-selected={search.tab === tab.id}
              key={tab.id}
              className={`report-tab ${search.tab === tab.id ? 'on' : ''}`}
              onKeyDown={(event) => {
                if (event.key !== ' ') return;
                event.preventDefault();
                event.currentTarget.click();
              }}
            >
              {tab.label}
            </Link>
          ))}
        </TabStrip>
      </div>

      {!integrityError && trendQuery.data && <PerformanceKpis response={trendQuery.data} availability={availability}/>}

      {!integrityError && (
        <main className="performance-content">
          <PerformanceTrendPanel
            points={points}
            metric={search.trendMetric}
            onMetricChange={(trendMetric) => onSearchChange({ trendMetric }, { replace: true })}
          />
          {search.tab === 'overview' ? (
            <Fragment>
              <ChannelMix
                response={channelQuery.data}
                metric={search.channelMixMetric}
                onMetricChange={(channelMixMetric) => onSearchChange({ channelMixMetric }, { replace: true })}
              />
              <PerformanceTable
                title="Top productions"
                tab="production"
                response={productionQuery.data}
                query={productionQuery}
                search={search}
                onSearchChange={onSearchChange}
              />
            </Fragment>
          ) : (
            <PerformanceTable
              title={tabTitle}
              tab={search.tab}
              response={tableQuery.data}
              query={tableQuery}
              search={search}
              onSearchChange={onSearchChange}
            />
          )}
        </main>
      )}
    </div>
  );
}
